import { ReactNode } from 'react';
import { Typography } from '@mui/material';
import { TattooChoices } from '@prisma/client';
import {
  endOfDay,
  endOfMonth,
  endOfWeek,
  format,
  getDay,
  isSameDay,
  parseISO,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import { enUS } from 'date-fns/locale';
import { DateLocalizer, dateFnsLocalizer, View } from 'react-big-calendar';
import { formatInTimeZone, getTimezoneOffset } from 'date-fns-tz';
import {
  Appointment,
  EmployeeToBusiness,
  FilterOption,
  UnavailableTimes,
} from '../../interfaces';
import {
  convertDateToOnschedHours,
  convertIndexToShortDay,
  convertOnschedHoursToDateFromOffset,
  formatDollarAmount,
  getCustomerNameFromAppointment,
  getInitials,
  getFullTattooLocation,
  isMinor,
} from '../../utils/global/helper';
import {
  CALENDAR_EVENT_TYPES,
  CalendarEvent,
  CalendarEventType,
  DEPOSIT_STATUS_VALUES,
  DepositFilterStatus,
  Resource,
} from '../../types/calendar';
import { GetAllInPrice } from '../../hooks/payments/useCashDiscount';
import {
  AppointmentStatusKey,
  getAppointmentStatusKey,
  getCustomerDetails,
  isAppointmentDepositPaid,
} from '../appointments';
import { isPaymentMethodSubjectToFees } from '../payments';
import { getWaiverRequestStatus } from '../waivers';
import { GetWaiversData } from '../../types/waivers';
import {
  EmployeesResourceMap,
  GetBatchTimeBlocksResponse,
  GetBatchTripsResponse,
  ResourceBlock,
} from '../../types/employees';
import { BusinessFlashDay } from '../../types/flashbooks';
import {
  getAllBlockedDaysFromTimeBlock,
  getAllDaysFromTrip,
  getTimeBlockTimes,
} from '../employees';
import { OnSchedCommonType } from '../../types/on-sched';
import { DEFAULT_TIMEZONE } from '../../constants/appointments';
import { FILTER_PERSISTENCE_KEYS } from '../../constants/global';
import {
  DEFAULT_EVENT_CALENDAR_TYPES,
  DEFAULT_WAIVER_STATUSES,
  UNASSIGNED_RESOURCE_COLOR,
  UNASSIGNED_RESOURCE_ID,
  WAIVER_STATUS_VALUES,
  WaiverFilterStatus,
} from '../../constants/calendar';

export const STATUS_FILL_BY_KEY: Record<AppointmentStatusKey, string> = {
  unconfirmed: 'var(--status-unconfirmed-fill)',
  confirmed: 'var(--status-confirmed-fill)',
  completed: 'var(--status-completed-fill)',
  canceled: 'var(--status-canceled-fill)',
  noShow: 'var(--status-noshow-fill)',
};

export const STATUS_TEXT_BY_KEY: Record<AppointmentStatusKey, string> = {
  unconfirmed: 'var(--status-unconfirmed-text)',
  confirmed: 'var(--status-confirmed-text)',
  completed: 'var(--status-completed-text)',
  canceled: 'var(--status-canceled-text)',
  noShow: 'var(--status-noshow-text)',
};

// Wash opacity varies by status, so it's threaded through like fill/text
// rather than living as a fixed value in the CSS class.
export const STATUS_TAG_WASH_BY_KEY: Record<AppointmentStatusKey, string> = {
  unconfirmed: 'var(--tag-wash-unconfirmed)',
  confirmed: 'var(--tag-wash-confirmed)',
  completed: 'var(--tag-wash-completed)',
  canceled: 'var(--tag-wash-canceled)',
  noShow: 'var(--tag-wash-noshow)',
};

// Collapses getWaiverRequestStatus's 6 states down to the 3 the calendar
// block shows. No waiverRequest at all means no icon at all.
export function getWaiverIconColor(
  appointment: Appointment,
  waivers: GetWaiversData[] | undefined | null,
): string | null {
  if (!appointment.waiverRequest) return null;
  const status = getWaiverRequestStatus({
    appointment,
    waiverRequest: appointment.waiverRequest,
    waivers,
  });
  if (status === 'pendingEmployee') return 'var(--waiver-icon-pending)';
  if (status === 'complete') return 'var(--waiver-icon-complete)';
  return 'var(--waiver-icon-otherwise)';
}

// Consultation blocks swap to their status's deep fill so the white hatch
// (styles.css `.type-consultation`) stays visible; canceled has none.
const STATUS_DEEP_FILL_BY_KEY: Partial<Record<AppointmentStatusKey, string>> = {
  unconfirmed: 'var(--status-unconfirmed-deep-fill)',
  confirmed: 'var(--status-confirmed-deep-fill)',
  completed: 'var(--status-completed-deep-fill)',
  noShow: 'var(--status-noshow-deep-fill)',
};

export const getVisibleRange = (
  view: View,
  date: Date,
): { start: Date; end: Date } => {
  if (view === 'day') return { start: startOfDay(date), end: endOfDay(date) };
  if (view === 'week')
    return { start: startOfWeek(date), end: endOfWeek(date) };
  return { start: startOfMonth(date), end: endOfMonth(date) };
};

export const filterAppointmentsToRange = (
  appointments: Appointment[] | undefined,
  range: { start: Date; end: Date },
): Appointment[] => {
  if (!appointments) return [];
  const startMs = range.start.getTime();
  const endMs = range.end.getTime();
  return appointments.filter(a => {
    // Booking requests without a start are always considered "visible".
    if (!a.startDateTime) return true;
    const start = new Date(a.startDateTime).getTime();
    const end = a.endDateTime ? new Date(a.endDateTime).getTime() : start;
    // Interval overlap: [start, end] intersects [startMs, endMs].
    return start <= endMs && end >= startMs;
  });
};

// All-in FIXED price: two fees when the deposit was paid via a fee-bearing
// method OR the appointment is still pending (deposit still expected);
// one fee when no deposit was collected and checkout is complete.
const getFixedAllInPrice = (
  appointment: Appointment,
  getAllInPrice: GetAllInPrice,
) => {
  const projectTwoFees = isAppointmentDepositPaid(appointment)
    ? isPaymentMethodSubjectToFees(appointment.depositPaymentMethod)
    : !appointment.checkoutSessionCompleted;
  return getAllInPrice(appointment.price ?? 0, {
    depositPrice: projectTwoFees ? appointment.depositPrice ?? 0 : 0,
  });
};

// Compact price string, no classic mode's narrative suffix.
export const getRedesignPriceText = (
  appointment: Appointment,
  getAllInPrice: GetAllInPrice,
) => {
  const { priceType, minPrice, maxPrice, hourlyPrice } = appointment;
  if (priceType === 'HOURLY') {
    return `${formatDollarAmount(getAllInPrice(hourlyPrice ?? 0))} per hour`;
  }
  if (priceType === 'RANGE') {
    return `${formatDollarAmount(
      getAllInPrice(minPrice ?? 0),
    )} - ${formatDollarAmount(getAllInPrice(maxPrice ?? 0))}`;
  }
  return formatDollarAmount(getFixedAllInPrice(appointment, getAllInPrice));
};

export const getAppointmentDisplayText = (
  appointment: Appointment,
  getAllInPrice: GetAllInPrice,
) => {
  const {
    tattooLocation,
    bodySide,
    tattooSize,
    priceType,
    minPrice,
    maxPrice,
    hourlyPrice,
  } = appointment;
  const fullTattooLocation = getFullTattooLocation(tattooLocation, bodySide);

  let priceText = '';
  if (priceType === 'HOURLY') {
    priceText = `${formatDollarAmount(
      getAllInPrice(hourlyPrice ?? 0),
    )} per hour.`;
  }
  if (priceType === 'FIXED') {
    priceText = `${formatDollarAmount(
      getFixedAllInPrice(appointment, getAllInPrice),
    )} for appointment.`;
  }
  if (priceType === 'RANGE') {
    priceText = `Price range of ${formatDollarAmount(
      getAllInPrice(minPrice ?? 0),
    )} - ${formatDollarAmount(getAllInPrice(maxPrice ?? 0))}.`;
  }

  let detailsText = '';
  if (!fullTattooLocation && !tattooSize) {
    detailsText = 'No details added for appointment.';
  } else if (fullTattooLocation && !tattooSize) {
    detailsText = `${fullTattooLocation} tattoo.`;
  } else if (!fullTattooLocation && tattooSize) {
    detailsText = `${tattooSize} tattoo.`;
  } else {
    detailsText = `${tattooSize} tattoo on clients ${fullTattooLocation}.`;
  }

  return `${detailsText} ${priceText}`;
};

const SERVICE_CATEGORY_LABELS: Record<string, string> = {
  TATTOO: 'Tattoo',
  PIERCING: 'Piercing',
  PERMANENT_MAKEUP: 'Permanent Makeup',
  SPA: 'Spa',
};

// Design spec calls these "request tags" — 18px pills on a translucent white
// wash, no border. Order is placement, size, color.
function RequestTagPill({
  textColor,
  wash,
  className,
  children,
}: {
  textColor: string;
  wash: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <span className={className} style={{ color: textColor, background: wash }}>
      <span
        style={{
          display: 'inline-block',
          maxWidth: '100%',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          verticalAlign: 'top',
        }}>
        {children}
      </span>
    </span>
  );
}

function MinorToken({ textColor }: { textColor: string }) {
  return (
    <span
      className="appt-minor"
      style={{ borderColor: textColor, color: textColor }}>
      Minor
    </span>
  );
}

export const getAppointmentDetailsComponent = (
  appointment: Appointment,
  sizeOptions: TattooChoices[] | undefined,
  getAllInPrice: GetAllInPrice,
  // `redesignActive` switches the whole layout: pills/Minor token below vs.
  // classic's plain sentences.
  textColor: string = 'white',
  redesignActive = false,
  appointmentIsMinor = false,
  tagWash: string = 'var(--tag-wash-confirmed)',
) => {
  const {
    tattooLocation,
    bodySide,
    tattooSize,
    tattooColor,
    priceType,
    minPrice,
    maxPrice,
    hourlyPrice,
    serviceCategory,
    tattooDetails,
  } = appointment;
  const fullTattooLocation = getFullTattooLocation(tattooLocation, bodySide);
  const sizeItem = sizeOptions?.find(option => option.value === tattooSize);
  const sizeLabel = sizeItem?.name ?? tattooSize;

  if (redesignActive) {
    const redesignPriceText = getRedesignPriceText(appointment, getAllInPrice);
    const tags = [
      SERVICE_CATEGORY_LABELS[serviceCategory || ''] || 'Other Service',
      fullTattooLocation,
      serviceCategory === 'TATTOO' ? sizeLabel : undefined,
      serviceCategory === 'TATTOO' ? tattooColor : undefined,
    ].filter((tag): tag is string => !!tag);

    const overflowCount = tags.length - 1;
    return (
      <>
        <div className="appt-summary-line">
          <Typography
            variant="body2"
            className="appt-price-summary"
            color={textColor}
            style={{
              lineHeight: 'normal',
              fontSize: 'var(--appt-price-size, 12px)',
              fontWeight: 700,
            }}>
            {redesignPriceText}
          </Typography>
          {appointmentIsMinor && <MinorToken textColor={textColor} />}
        </div>
        {!!tags.length && (
          <div className="appt-tags-row">
            {tags.map(tag => (
              <RequestTagPill
                key={tag}
                textColor={textColor}
                wash={tagWash}
                className="appt-tag">
                {tag}
              </RequestTagPill>
            ))}
            {overflowCount > 0 && (
              <RequestTagPill
                textColor={textColor}
                wash={tagWash}
                className="appt-tag-overflow">
                +{overflowCount}
              </RequestTagPill>
            )}
          </div>
        )}
        {!!tattooDetails && (
          <Typography
            variant="body2"
            className="appt-description"
            color={textColor}
            style={{
              lineHeight: 1.4,
              fontSize: 11,
              flexShrink: 1,
              minHeight: 0,
              overflow: 'hidden',
            }}>
            {tattooDetails}
          </Typography>
        )}
      </>
    );
  }

  let priceText = '';
  if (priceType === 'HOURLY') {
    priceText = `${formatDollarAmount(
      getAllInPrice(hourlyPrice ?? 0),
    )} per hour.`;
  }
  if (priceType === 'FIXED') {
    priceText = `${formatDollarAmount(
      getFixedAllInPrice(appointment, getAllInPrice),
    )} quote.`;
  }
  if (priceType === 'RANGE') {
    priceText = `${formatDollarAmount(
      getAllInPrice(minPrice ?? 0),
    )} - ${formatDollarAmount(getAllInPrice(maxPrice ?? 0))} quote.`;
  }

  let detailsText = '';
  if (!fullTattooLocation && !tattooSize) {
    detailsText = 'No details added for appointment.';
  } else if (fullTattooLocation && !tattooSize) {
    detailsText = `${fullTattooLocation} tattoo.`;
  } else if (!fullTattooLocation && tattooSize) {
    detailsText = `${sizeLabel} tattoo.`;
  } else {
    detailsText = `${sizeLabel} tattoo on ${fullTattooLocation}.`;
  }

  if (serviceCategory !== 'TATTOO') {
    const serviceLabel =
      SERVICE_CATEGORY_LABELS[serviceCategory || ''] || 'Other Service';
    return (
      <Typography
        variant="body2"
        color={textColor}
        style={{ lineHeight: 'normal' }}>
        {serviceLabel}
      </Typography>
    );
  }

  return (
    <>
      <Typography
        variant="body2"
        color={textColor}
        style={{ lineHeight: 'normal' }}>
        {detailsText}
      </Typography>
      <Typography
        variant="body2"
        color={textColor}
        style={{ lineHeight: 'normal' }}>
        {priceText}
      </Typography>
      <Typography
        variant="body2"
        color={textColor}
        style={{ lineHeight: 'normal' }}>
        {tattooDetails}
      </Typography>
    </>
  );
};

export const getEmployeeTimezoneOffset = (
  employeeId: string,
  employeesResourceMap?: EmployeesResourceMap,
) => {
  const employeeResource = employeesResourceMap?.[employeeId];
  const employeeTimezoneOffset = employeeResource?.userDbTimezone
    ? getTimezoneOffset(employeeResource?.userDbTimezone) / 60000
    : employeeResource?.timezoneOffset;
  return employeeTimezoneOffset;
};

const getTimeBlockDetailsComponent = (
  block: ResourceBlock,
  textColor: string,
) => {
  const isAllDay = block.startTime === 0 && block.endTime === 2400;
  if (isAllDay) {
    return null;
  }
  return (
    <>
      <Typography
        variant="body2"
        sx={{ lineHeight: 'normal', color: textColor }}>
        {block.reason}
      </Typography>
      <Typography
        variant="body2"
        sx={{ lineHeight: 'normal', color: textColor }}>
        {block.repeat ? 'Time block repeats' : 'Does not repeat'}
      </Typography>
    </>
  );
};

export const getEmployeeCalendarName = (
  employees: EmployeeToBusiness[],
  employeeId: string,
) => {
  const employee = employees.find(e => e.employeeId === employeeId);
  if (!employee) return '';
  return (
    employee.displayName ||
    getInitials({
      firstName: employee.user?.firstName,
      lastName: employee.user?.lastName,
    })
  );
};

export const getAppointmentWaiverFilterStatus = (
  appointment: Appointment,
): WaiverFilterStatus => {
  const { waiverRequest } = appointment;
  if (!waiverRequest) return WAIVER_STATUS_VALUES.NO_WAIVER;
  if (waiverRequest.status === 'CANCELED')
    return WAIVER_STATUS_VALUES.NO_WAIVER;
  if (waiverRequest.status === 'SIGNED' && waiverRequest.signedFileLink) {
    return WAIVER_STATUS_VALUES.SIGNED;
  }
  return WAIVER_STATUS_VALUES.PENDING;
};

// rbc's sort callback only sees start/end/allDay, so appointment start dates carry a tag
type EventStartDate = Date & { isAppointment?: boolean };

type SortableEvent = { start: EventStartDate; end: Date; allDay?: boolean };

// appointments first, then everything sorted by time
const sortCalendarEvents = ({
  evtA,
  evtB,
}: {
  evtA: SortableEvent;
  evtB: SortableEvent;
}): number => {
  const aIsAppointment = !!evtA.start.isAppointment;
  const bIsAppointment = !!evtB.start.isAppointment;
  if (aIsAppointment !== bIsAppointment) return aIsAppointment ? -1 : 1;
  if (+evtA.start !== +evtB.start) return +evtA.start - +evtB.start;
  return +evtA.end - +evtB.end;
};

export const getDateFnsLocalizer = (): DateLocalizer => {
  const localizer = dateFnsLocalizer({
    format,
    parse: parseISO,
    startOfWeek,
    getDay,
    locales: { 'en-US': enUS },
  });
  // the cast is needed because rbc's types misdeclare sortEvents; at runtime it receives { evtA, evtB }
  localizer.sortEvents =
    sortCalendarEvents as unknown as DateLocalizer['sortEvents'];
  return localizer;
};

export const getAppointmentDepositFilterStatus = (
  appointment: Appointment,
): Exclude<DepositFilterStatus, typeof DEPOSIT_STATUS_VALUES.ALL> => {
  if (isAppointmentDepositPaid(appointment) && !appointment.isDepositRefunded) {
    return DEPOSIT_STATUS_VALUES.PAID;
  }
  if (appointment.depositPrice) return DEPOSIT_STATUS_VALUES.UNPAID;
  return DEPOSIT_STATUS_VALUES.NO_DEPOSIT;
};

export const getCalendarEvents = ({
  appointments,
  timeBlocks: passedTimeBlocks,
  trips: passedTrips,
  flashDays: passedFlashDays,
  unavailableTimes: passedUnavailableTimes,
  resourceMap,
  sizeOptions,
  employeesToBusiness,
  employeesResourceMap,
  selectedEvents,
  getAllInPrice,
  redesignActive,
  waivers,
}: {
  appointments: Appointment[];
  timeBlocks?: GetBatchTimeBlocksResponse;
  trips?: GetBatchTripsResponse;
  flashDays?: BusinessFlashDay[];
  unavailableTimes?: Record<string, UnavailableTimes[]>;
  employeesToBusiness: EmployeeToBusiness[];
  resourceMap?: Resource[];
  sizeOptions?: TattooChoices[];
  employeesResourceMap?: EmployeesResourceMap;
  selectedEvents: CalendarEventType[];
  getAllInPrice: GetAllInPrice;
  // ENG-2793: when true, fill/text encode status instead of artist identity.
  redesignActive?: boolean;
  waivers?: GetWaiversData[] | null;
}): CalendarEvent[] => {
  const includeCanceledAppointments = selectedEvents.includes(
    CALENDAR_EVENT_TYPES.CANCELLED_APPOINTMENTS,
  );
  const includeScheduledAppointments = selectedEvents.includes(
    CALENDAR_EVENT_TYPES.SCHEDULED_APPOINTMENTS,
  );

  const getAppointmentTitlePrefix = (appointment: Appointment): string => {
    if (appointment.isVirtual && appointment.appointmentType === 'CONSULTATION')
      return appointment.virtualMeetingProvider === 'PHONE'
        ? 'Phone: '
        : 'Virtual: ';
    if (appointment.appointmentType === 'CONSULTATION') return 'Consult: ';
    if (appointment.appointmentType === 'WALK_IN') return 'Walk-In: ';
    return '';
  };

  const filteredAppointments = appointments.filter(appointment => {
    if (appointment.status === 'CANCELED' && !includeCanceledAppointments) {
      return false;
    }
    if (
      (appointment.status === 'SCHEDULED' ||
        appointment.status === 'CONFIRMED') &&
      !includeScheduledAppointments
    ) {
      return false;
    }
    return true;
  });

  const appointmentEvents: CalendarEvent[] =
    filteredAppointments?.map(appointment => {
      const titlePrefix = getAppointmentTitlePrefix(appointment);
      const customerName = getCustomerNameFromAppointment(appointment);
      const start: EventStartDate = parseISO(appointment.startDateTime!);
      start.isAppointment = true;
      const resourceColor =
        resourceMap?.find(
          resource =>
            resource.resourceId ===
            (appointment.employeeId ?? UNASSIGNED_RESOURCE_ID),
        )?.color ??
        (appointment.employeeId ? 'blue' : UNASSIGNED_RESOURCE_COLOR);
      const statusKey = redesignActive
        ? getAppointmentStatusKey(appointment)
        : undefined;
      const isConsultation = appointment.appointmentType === 'CONSULTATION';
      const statusFill =
        statusKey &&
        (isConsultation
          ? STATUS_DEEP_FILL_BY_KEY[statusKey] ?? STATUS_FILL_BY_KEY[statusKey]
          : STATUS_FILL_BY_KEY[statusKey]);
      const attendeeDob = getCustomerDetails(appointment).dob;
      const appointmentIsMinor = isMinor(
        attendeeDob,
        appointment.startDateTime,
      );
      const waiverIconColor = redesignActive
        ? getWaiverIconColor(appointment, waivers)
        : undefined;
      const priceText = redesignActive
        ? getRedesignPriceText(appointment, getAllInPrice)
        : undefined;
      return {
        type: 'appointment',
        start,
        end: parseISO(appointment.endDateTime!),
        title: `${titlePrefix}${customerName}`,
        description: getAppointmentDisplayText(appointment, getAllInPrice),
        detailsComponent: getAppointmentDetailsComponent(
          appointment,
          sizeOptions,
          getAllInPrice,
          statusKey ? STATUS_TEXT_BY_KEY[statusKey] : undefined,
          redesignActive,
          appointmentIsMinor,
          statusKey ? STATUS_TAG_WASH_BY_KEY[statusKey] : undefined,
        ),
        allDay: false,
        appointmentId: appointment.appointmentId,
        resourceId: appointment.employeeId ?? UNASSIGNED_RESOURCE_ID,
        // Redesign: fill/text encode status. Classic: fill encodes artist identity.
        color: statusFill || resourceColor,
        textColor: statusKey ? STATUS_TEXT_BY_KEY[statusKey] : undefined,
        canceled: appointment.status === 'CANCELED',
        checkedOut: appointment.checkoutSessionCompleted,
        serviceCategory: appointment.serviceCategory,
        appointmentType: appointment.appointmentType,
        isVirtual: appointment.isVirtual,
        statusKey,
        waiverIconColor,
        railColor: redesignActive ? resourceColor : undefined,
        priceText,
        isMinor: redesignActive ? appointmentIsMinor : undefined,
      };
    }) || [];

  const unavailableColor = redesignActive
    ? 'text.secondary'
    : 'rgba(255, 0, 0, .6)';
  const timeBlocks = selectedEvents.includes(CALENDAR_EVENT_TYPES.TIME_BLOCKS)
    ? passedTimeBlocks
    : undefined;

  const timeBlockEvents = (
    timeBlocks
      ? Object.entries(timeBlocks).map(([employeeId, blocks]) => {
          const employee = employeesToBusiness.find(
            e => e.employeeId === employeeId,
          );
          if (!employee) return [];

          const employeeName = getEmployeeCalendarName(
            employeesToBusiness,
            employeeId,
          );

          const localTimezone =
            Intl.DateTimeFormat().resolvedOptions().timeZone;

          const employeeTimezoneOffset = getEmployeeTimezoneOffset(
            employeeId,
            employeesResourceMap,
          );

          const employeeTimezone = employee.user?.timezone || localTimezone;

          const list = blocks.flatMap(block => {
            const allDates = getAllBlockedDaysFromTimeBlock(block);
            if (!allDates) return [];

            return allDates.map(date => {
              const { start: startDateTime, end: endDateTime } =
                getTimeBlockTimes({
                  timeBlock: block,
                  date,
                  employeeTimezone,
                  employeeTimezoneOffset,
                });

              const isAllDay = block.startTime === 0 && block.endTime === 2400;

              return {
                type: 'time-block',
                timeBlockId: block.id,
                start: parseISO(startDateTime.toISOString()),
                end: parseISO(endDateTime.toISOString()),
                title: `${employeeName} - Time Block`,
                description: block.reason || '',
                detailsComponent: getTimeBlockDetailsComponent(
                  block,
                  unavailableColor,
                ),
                allDay: isAllDay,
                color: '#fff',
                textColor: unavailableColor,
                resourceId: employeeId,
              } as CalendarEvent;
            });
          });

          return list;
        })
      : []
  ).flat();

  const trips = selectedEvents.includes(CALENDAR_EVENT_TYPES.TRIPS)
    ? passedTrips
    : undefined;

  const tripEvents = (
    trips
      ? Object.entries(trips).map(([employeeId, employeeTrips]) => {
          const employee = employeesToBusiness.find(
            e => e.employeeId === employeeId,
          );
          if (!employee) return [];

          const employeeInitials = `${employee.user?.firstName[0] || ''}${
            employee.user?.lastName[0] || ''
          }`;

          const list = employeeTrips.flatMap(trip => {
            const allDates = getAllDaysFromTrip(trip);
            if (!allDates) return [];

            return allDates.map(date => {
              const dateStringWithTime = `${date}T00:00:00`;
              const startDateTime = new Date(dateStringWithTime);
              const endDateTime = new Date(dateStringWithTime);

              startDateTime.setHours(0, 0);
              endDateTime.setHours(23, 59);

              return {
                type: 'trip',
                tripId: trip.tripId,
                start: parseISO(startDateTime.toISOString()),
                end: parseISO(endDateTime.toISOString()),
                title: `${employeeInitials} - Trip`,
                description: '',
                allDay: true,
                color: '#fff',
                textColor: redesignActive
                  ? 'var(--nonstatus-trip-text)'
                  : '#CCCC66',
                resourceId: employeeId,
              } as CalendarEvent;
            });
          });

          return list;
        })
      : []
  ).flat();

  const flashDays = selectedEvents.includes(CALENDAR_EVENT_TYPES.FLASH_DAYS)
    ? passedFlashDays
    : undefined;

  const flashDayEvents = (
    flashDays
      ? flashDays.flatMap(flashDay => {
          const dateString = formatInTimeZone(
            parseISO(flashDay.date),
            flashDay.timezone,
            'yyyy-MM-dd',
          );
          const dateStringWithTime = `${dateString}T00:00:00`;
          const startDateTime = new Date(dateStringWithTime);
          const endDateTime = new Date(dateStringWithTime);

          startDateTime.setHours(0, 0);
          endDateTime.setHours(23, 59);

          return [
            {
              type: 'flash-day',
              flashDayId: flashDay.id,
              start: parseISO(startDateTime.toISOString()),
              end: parseISO(endDateTime.toISOString()),
              title: flashDay.name || 'Flash Day',
              description: '',
              allDay: true,
              color: '#fff',
              textColor: redesignActive
                ? 'var(--nonstatus-flash-text)'
                : '#8BC34A',
              resourceId: employeesToBusiness.map(
                employee => employee.employeeId,
              ) as unknown as string,
            } as CalendarEvent,
          ];
        })
      : []
  ).flat();

  const unavailableTimes = selectedEvents.includes(
    CALENDAR_EVENT_TYPES.UNAVAILABLE_TIMES,
  )
    ? passedUnavailableTimes
    : undefined;

  const unavailableTimeEvents = Object.entries(unavailableTimes ?? {})
    .flatMap(([employeeId, employeeUnavailableTimes]) =>
      employeeUnavailableTimes.map(unavailableTime => {
        const isAppointment = unavailableTime.entityType === 'APT';
        if (isAppointment) return null;
        const isAllDay =
          unavailableTime.fromTime === 0 && unavailableTime.toTime === 2400;
        const employeeName = getEmployeeCalendarName(
          employeesToBusiness,
          employeeId,
        );
        return {
          type: 'unavailable-time',
          unavailableTimeId: unavailableTime.id,
          start: parseISO(unavailableTime.startDateTime),
          end: parseISO(unavailableTime.endDateTime),
          title: `${employeeName} - Google Calendar Event`,
          detailsComponent: (
            <Typography variant="body2" color={unavailableColor}>
              {unavailableTime.reason || 'Unavailable'}
            </Typography>
          ),
          allDay: isAllDay,
          color: '#fff',
          textColor: unavailableColor,
          resourceId: employeeId,
          showSourceLabel: redesignActive,
        } as CalendarEvent;
      }),
    )
    .filter(Boolean) as CalendarEvent[];

  return appointmentEvents
    .concat(timeBlockEvents)
    .concat(tripEvents)
    .concat(flashDayEvents)
    .concat(unavailableTimeEvents);
};

export const slotPropGetter = (
  date: Date,
  resourceId: number | string | undefined,
  employeesResourceMap: EmployeesResourceMap = {},
  timeBlocks: GetBatchTimeBlocksResponse = {},
  redesignActive = false,
) => {
  const closedHoursColor = redesignActive
    ? 'var(--nonstatus-closed-hours-bg)'
    : '#e0e0e0';

  if (!resourceId) return {};

  const dayIndex = getDay(date);
  const previousDayIndex = dayIndex === 0 ? 6 : dayIndex - 1;

  const employeeResource = employeesResourceMap[resourceId as string];
  if (!employeeResource) return {};

  const { availability } = employeeResource;
  if (!availability) return {};

  const day = convertIndexToShortDay(dayIndex);
  const previousDay = convertIndexToShortDay(previousDayIndex);

  const dayAvailability = availability[day];
  const previousDayAvailability = availability[previousDay];

  if (!dayAvailability) return {};

  const isOpenAllDay =
    dayAvailability.startTime === 0 && dayAvailability.endTime === 2400;
  if (isOpenAllDay) return {};

  const employeeTimeBlocks = timeBlocks[resourceId as string];
  const isClosedAllDay =
    employeeTimeBlocks &&
    employeeTimeBlocks.find(
      timeBlock =>
        timeBlock.startTime === 0 &&
        timeBlock.endTime === 2400 &&
        dayIndex === getDay(parseISO(timeBlock.startDate)) &&
        isSameDay(parseISO(timeBlock.startDate), date),
    );

  if (isClosedAllDay)
    return {
      style: {
        backgroundColor: closedHoursColor,
        borderColor: closedHoursColor,
      },
    };

  const employeeTimezoneOffset = getEmployeeTimezoneOffset(
    resourceId as string,
    employeesResourceMap,
  );

  const defaultTimezoneOffset = getTimezoneOffset(DEFAULT_TIMEZONE) / 60000;

  const timezoneOffset = employeeTimezoneOffset ?? defaultTimezoneOffset;

  const [startClosedTime, endClosedTime] = [
    convertDateToOnschedHours(
      convertOnschedHoursToDateFromOffset(
        dayAvailability.endTime,
        timezoneOffset,
      ),
    ),
    convertDateToOnschedHours(
      convertOnschedHoursToDateFromOffset(
        dayAvailability.startTime,
        timezoneOffset,
      ),
    ),
  ];
  const [previousDayStartClosedTime, previousDayEndClosedTime] = [
    convertDateToOnschedHours(
      convertOnschedHoursToDateFromOffset(
        previousDayAvailability?.endTime ?? 0,
        timezoneOffset,
      ),
    ),
    convertDateToOnschedHours(
      convertOnschedHoursToDateFromOffset(
        previousDayAvailability?.startTime ?? 0,
        timezoneOffset,
      ),
    ),
  ];

  const dateOnschedHours = convertDateToOnschedHours(date);
  const goesToNextDay = startClosedTime < endClosedTime;

  const previousDayCarriesOver =
    !!previousDayAvailability &&
    previousDayStartClosedTime < previousDayEndClosedTime;

  const isOpenFromPreviousDay =
    previousDayCarriesOver && dateOnschedHours < previousDayStartClosedTime;

  const isCurrentTimeClosed =
    !isOpenFromPreviousDay &&
    (startClosedTime === endClosedTime ||
      (goesToNextDay
        ? // only include slots in current day
          dateOnschedHours < endClosedTime
        : dateOnschedHours >= startClosedTime ||
          dateOnschedHours < endClosedTime));

  if (!isCurrentTimeClosed) return {};

  return {
    style: {
      backgroundColor: closedHoursColor,
      borderColor: closedHoursColor,
    },
  };
};

export const getEarliestAvailabilities = (
  employeesResourceMap: EmployeesResourceMap,
  selectedArtists?: string[],
) => {
  const earliestAvailabilities: Record<string, Date> = {};
  Object.entries(employeesResourceMap).forEach(
    ([employeeId, employeeResource]) => {
      if (selectedArtists && !selectedArtists.includes(employeeId)) return;

      const { availability, timezoneOffset, userDbTimezone } = employeeResource;
      if (!availability) return;

      const newTimezoneOffset = userDbTimezone
        ? getTimezoneOffset(userDbTimezone) / 60000
        : timezoneOffset;

      Object.entries(availability).forEach(([day, dayAvailability]) => {
        if (!dayAvailability) return;

        const { startTime = 0, endTime = 0 } =
          dayAvailability as OnSchedCommonType.Time;

        const isDayClosed = startTime === 0 && endTime === 0;
        if (isDayClosed) return;

        const startTimeDate = convertOnschedHoursToDateFromOffset(
          startTime,
          newTimezoneOffset,
        );

        const currentEarliest = earliestAvailabilities[day];

        if (
          !currentEarliest ||
          startTimeDate.getTime() < currentEarliest.getTime()
        ) {
          earliestAvailabilities[day] = startTimeDate;
        }
      });
    },
  );

  return earliestAvailabilities;
};

export const getInitialCalendarEvents = () => {
  try {
    const key = FILTER_PERSISTENCE_KEYS.calendarEvents;
    const cachedEvents = sessionStorage.getItem(key);
    if (!cachedEvents) return DEFAULT_EVENT_CALENDAR_TYPES;
    const parsed = JSON.parse(cachedEvents);
    if (!Array.isArray(parsed)) return DEFAULT_EVENT_CALENDAR_TYPES;

    return parsed.filter(event =>
      Object.values(CALENDAR_EVENT_TYPES).includes(event),
    );
  } catch (error) {
    return DEFAULT_EVENT_CALENDAR_TYPES;
  }
};

export const getInitialWaiverStatuses = (): WaiverFilterStatus[] => {
  try {
    const key = FILTER_PERSISTENCE_KEYS.calendarWaiverStatus;
    const cached = sessionStorage.getItem(key);
    if (!cached) return DEFAULT_WAIVER_STATUSES;
    const parsed = JSON.parse(cached);
    if (!Array.isArray(parsed)) return DEFAULT_WAIVER_STATUSES;

    return parsed.filter((s: string) =>
      Object.values(WAIVER_STATUS_VALUES).includes(s as WaiverFilterStatus),
    );
  } catch {
    return DEFAULT_WAIVER_STATUSES;
  }
};

export const getInitialTrips = (options: FilterOption[]): string[] => {
  const allValues = options.map(option => option.value);
  try {
    const key = FILTER_PERSISTENCE_KEYS.calendarTrips;
    const cached = sessionStorage.getItem(key);
    if (!cached) return allValues;
    const parsed = JSON.parse(cached);
    if (!Array.isArray(parsed)) return allValues;

    return parsed.filter((trip: string) => allValues.includes(trip));
  } catch {
    return allValues;
  }
};

export const getInitialIncludeInactiveEmployees = (): boolean => {
  try {
    const key = FILTER_PERSISTENCE_KEYS.calendarInactiveEmployees;
    const cached = sessionStorage.getItem(key);
    if (!cached) return true;

    return JSON.parse(cached) !== 'false';
  } catch {
    return true;
  }
};

export const getInitialDepositStatus = (): DepositFilterStatus => {
  try {
    const key = FILTER_PERSISTENCE_KEYS.calendarDepositStatus;
    const cached = sessionStorage.getItem(key);
    if (!cached) return DEPOSIT_STATUS_VALUES.ALL;
    const parsed = JSON.parse(cached);
    if (Object.values(DEPOSIT_STATUS_VALUES).includes(parsed)) return parsed;
    return DEPOSIT_STATUS_VALUES.ALL;
  } catch {
    return DEPOSIT_STATUS_VALUES.ALL;
  }
};
